import { type JSX, useState } from "react"

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".")
  return dot === -1 ? "txt" : name.slice(dot + 1).toLowerCase()
}

// What a file may be called. The caller adds context-specific rules
// (duplicates, at minimum) via the names it passes in `siblings`.
export function validateFileName(next: string, siblings: string[]): string | null {
  if (next.length > 120) return "Name is too long."
  if (/[/\\]/.test(next)) return "Names can't contain slashes."
  if ([ ...next ].some((c) => c.charCodeAt(0) < 32)) return "Names can't contain control characters."
  if (next === "." || next === "..") return "That name is reserved."
  if (siblings.some((s) => s.toLowerCase() === next.toLowerCase())) return "A file with that name already exists."
  return null
}

// The header every file view shares: optional back link, extension chip,
// file name, meta line, the save-status note, and whatever action buttons
// the current mode calls for.
//
// `title` is the special-page override: when present it replaces the
// filename as the heading and marks the name as fixed — the objective shows
// "Objective", never "objective.md". Ordinary files (findings/...) omit it
// and, when `onRename` is provided, get click-to-rename on the filename —
// the same interaction as agent and workspace names. onRename returns an
// error message to show inline, or null on success.
export function FileHead({
  name,
  title,
  meta,
  note,
  onBack,
  onRename,
  actions,
}: {
  name: string
  title?: string
  meta: string
  note: string
  onBack?: () => void
  // Async because a real rename is a round trip to the sandbox. Resolves to
  // null when it landed, or the reason to show beside the field.
  onRename?: (next: string) => Promise<string | null>
  actions?: JSX.Element
}): JSX.Element {
  return (
    <header className="workspace__doc-head workspace__doc-head--file">
      <div>
        {onBack && (
          <button type="button" className="workspace__inline-link" onClick={onBack}>
            ← Findings
          </button>
        )}
        <h1 className="workspace__doc-title">
          {title ?? (onRename ? <RenameableName key={name} name={name} onRename={onRename} /> : name)}
        </h1>
        <p className="workspace__doc-meta">{meta}</p>
      </div>

      <div className="workspace__doc-actions">
        <span className="workspace__save-note">{note}</span>
        {actions}
      </div>
    </header>
  )
}

function RenameableName({
  name,
  onRename,
}: {
  name: string
  onRename: (next: string) => Promise<string | null>
}): JSX.Element {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const [error, setError] = useState<string | null>(null)

  function startEditing(): void {
    setDraft(name)
    setError(null)
    setEditing(true)
  }

  // An empty field is a cancel; a rejected name keeps the field open with
  // the reason shown beside it.
  async function commit(): Promise<void> {
    const next = draft.trim()
    if (!next || next === name) {
      setEditing(false)
      setError(null)
      return
    }
    const problem = await onRename(next)
    if (problem) {
      setError(problem)
      return
    }
    setEditing(false)
    setError(null)
  }

  if (!editing) {
    return (
      <button type="button" className="workspace__file-rename" onClick={startEditing} title="Click to rename">
        {name}
      </button>
    )
  }

  return (
    <>
      <input
        type="text"
        className="workspace__file-name-input"
        aria-label="File name"
        value={draft}
        maxLength={120}
        autoFocus
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault()
            void commit()
          } else if (event.key === "Escape") {
            event.preventDefault()
            setEditing(false)
            setError(null)
          }
        }}
      />
      {error && <span className="workspace__rename-error">{error}</span>}
    </>
  )
}
